import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { IotaModalOptions, IotaModalSize, MAT_DIALOG_DATA, MatDialogRef } from '@iota/iota-ui-components';
import { getValueFromStore } from '../../utils';
import { iotaConfigLocales } from '../../../store/reducer/iota-config.reducer';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { selectedLanguage } from '../../../store/reducer/language.reducer';

export interface TranslationDialogData {
    title: string;
    required: boolean;
    translations: Translation[];
    attributeKey: string;
    onSave: (attribute: string, translations: Translation[]) => Promise<void>;
}

export interface Translation {
    text: string;
    locale: string;
}

@Component({
    standalone: false,
    selector: 'app-translation-dialog',
    templateUrl: './translation-dialog.component.html',
    styleUrls: ['./translation-dialog.component.scss']
})
export class TranslationDialogComponent implements OnInit, OnDestroy {
    public modalOptions: Partial<IotaModalOptions> = {
        size: IotaModalSize.SIZE_3XL,
        showToggleFullScreenButton: true
    };

    private allLocales: string[] = [''];
    remainingLocales: string[] = [];
    remainingLocalesForIndex: { [idx: number]: string[] } = {};

    form: FormGroup = new FormGroup({
        translationsForm: new FormArray([])
    });

    isFullScreen = false;
    isAnimating = false;

    loading = true;

    get translationsForm(): FormArray {
        return this.form.controls['translationsForm'] as FormArray;
    }

    constructor(
        private store: Store,
        public dialogRef: MatDialogRef<TranslationDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: TranslationDialogData
    ) {}

    async ngOnInit() {
        setTimeout(() => {
            this.isAnimating = true;
        }, 100);
        this.loading = true;
        const locale = await getValueFromStore(this.store, selectedLanguage);

        if (!this.data.translations.length) {
            this.translationsForm.push(
                new FormGroup({
                    text: new FormControl(null, [Validators.required]),
                    locale: new FormControl(locale.countryCode, [Validators.required])
                })
            );
        }
        this.data.translations.forEach((it) =>
            this.translationsForm.push(
                new FormGroup({
                    text: new FormControl(it.text, [Validators.required]),
                    locale: new FormControl(it.locale, [Validators.required])
                })
            )
        );
        this.translationsForm.valueChanges.subscribe(() => this.calculatedRemainingLocales());
        this.init();
    }

    async init() {
        this.allLocales = await getValueFromStore(this.store, iotaConfigLocales);
        this.calculatedRemainingLocales();
        this.loading = false;
    }

    ngOnDestroy(): void {
        this.isAnimating = false;
    }

    addRow(): void {
        this.translationsForm.push(
            new FormGroup({
                text: new FormControl(null, [Validators.required]),
                locale: new FormControl(this.remainingLocales.shift(), [Validators.required])
            })
        );
    }

    deleteRow(idx: number): void {
        this.translationsForm.removeAt(idx);
    }

    onUpdateClick(): void {
        this.loading = true;
        this.data
            .onSave(this.data.attributeKey, this.translationsForm.value)
            .then(() => {
                this.dialogRef.close();
            })
            .catch(() => {
                window.alert($localize`There was a problem updating translations. Please try again.`);
            })
            .finally(() => (this.loading = false));
    }

    onCancelClick(): void {
        this.dialogRef.close();
    }

    toggleFullScreen() {
        this.isFullScreen = !this.isFullScreen;
    }

    private calculatedRemainingLocales() {
        const usedLocales = this.translationsForm.value.map((it) => it.locale);
        this.remainingLocales = [...this.allLocales].filter((it) => !usedLocales.includes(it));
        this.translationsForm.value.forEach((it, idx) => {
            const locales = [...this.remainingLocales];
            locales.unshift(it.locale);
            this.remainingLocalesForIndex[idx] = locales;
        });
    }
}
